import React, { useState } from 'react';

import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Save as SaveIcon,
  Send as SendIcon,
  TextFields as TextIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as DateIcon,
  AccessTime as TimeIcon,
  CheckBox as CheckboxIcon,
  RadioButtonChecked as RadioIcon,
  ArrowDropDown as SelectIcon,
  Notes as TextareaIcon,
  Draw as SignatureIcon,
  AttachFile as FileIcon,
  Person as PersonIcon,
  Article as ConsentIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  InputAdornment,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { permissionSlipApiService } from '../../services/permissionSlipApiService';
import { useAppSelector } from '../../store/hooks';
import { FieldType } from '../../types/permissionSlips';

const fieldTypeIcons: Record<string, React.ReactElement> = {
  [FieldType.TEXT]: <TextIcon />,
  [FieldType.EMAIL]: <EmailIcon />,
  [FieldType.PHONE]: <PhoneIcon />,
  [FieldType.DATE]: <DateIcon />,
  [FieldType.TIME]: <TimeIcon />,
  [FieldType.CHECKBOX]: <CheckboxIcon />,
  [FieldType.RADIO]: <RadioIcon />,
  [FieldType.SELECT]: <SelectIcon />,
  [FieldType.TEXTAREA]: <TextareaIcon />,
  [FieldType.SIGNATURE]: <SignatureIcon />,
  [FieldType.FILE_UPLOAD]: <FileIcon />,
  [FieldType.STUDENT_INFO]: <PersonIcon />,
  [FieldType.PARENT_INFO]: <PersonIcon />,
  [FieldType.CONSENT_TEXT]: <ConsentIcon />,
};

const fieldTypeLabels: Record<string, string> = {
  [FieldType.TEXT]: 'Text Input',
  [FieldType.EMAIL]: 'Email',
  [FieldType.PHONE]: 'Phone Number',
  [FieldType.DATE]: 'Date',
  [FieldType.TIME]: 'Time',
  [FieldType.CHECKBOX]: 'Checkbox',
  [FieldType.RADIO]: 'Radio Buttons',
  [FieldType.SELECT]: 'Dropdown',
  [FieldType.TEXTAREA]: 'Long Text',
  [FieldType.SIGNATURE]: 'Digital Signature',
  [FieldType.FILE_UPLOAD]: 'File Upload',
  [FieldType.STUDENT_INFO]: 'Student Information',
  [FieldType.PARENT_INFO]: 'Parent Information',
  [FieldType.CONSENT_TEXT]: 'Consent Statement',
};

interface FieldOption {
  label: string;
  value: string;
}

interface FieldConfig {
  tempId: string;
  field_type: FieldType;
  label: string;
  name: string;
  description?: string;
  placeholder?: string;
  help_text?: string;
  default_value?: unknown;
  is_required: boolean;
  is_visible: boolean;
  display_order: number;
  validation_rules?: Record<string, unknown>;
  options?: FieldOption[];
  metadata?: Record<string, unknown>;
}

export const CreateFormPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [editingField, setEditingField] = useState<FieldConfig | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'field_trip',
    requires_payment: false,
    payment_amount: 0,
    payment_description: '',
    expires_at: '',
    is_template: false,
    template_name: '',
    template_description: '',
  });

  const [fields, setFields] = useState<FieldConfig[]>([]);

  const steps = ['Basic Information', 'Form Fields', 'Settings & Review'];

  const categories = [
    { value: 'field_trip', label: 'Field Trip' },
    { value: 'sports', label: 'Sports & Athletics' },
    { value: 'medical', label: 'Medical' },
    { value: 'technology', label: 'Technology Use' },
    { value: 'media', label: 'Media Release' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'general', label: 'General' },
  ];

  const handleNext = () => {
    if (activeStep === 0 && !validateBasicInfo()) {
      return;
    }
    if (activeStep === 1 && fields.length === 0) {
      setError('Please add at least one field to the form');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateBasicInfo = () => {
    if (!formData.title.trim()) {
      setError('Please enter a form title');
      return false;
    }
    if (!formData.description.trim()) {
      setError('Please enter a form description');
      return false;
    }
    if (formData.requires_payment && formData.payment_amount <= 0) {
      setError('Please enter a valid payment amount');
      return false;
    }
    setError(null);
    return true;
  };

  const handleAddField = (type: FieldType) => {
    const newField: FieldConfig = {
      tempId: `field_${Date.now()}`,
      field_type: type,
      label: '',
      name: '',
      placeholder: '',
      help_text: '',
      is_required: false,
      is_visible: true,
      display_order: fields.length,
      validation_rules: {},
      options: (type === FieldType.RADIO || type === FieldType.SELECT) ? [] : undefined,
      default_value: '',
      metadata: {},
    };
    setEditingField(newField);
    setShowFieldPicker(false);
  };

  const handleSaveField = () => {
    if (!editingField) return;

    if (!editingField.label.trim()) {
      setError('Please enter a field label');
      return;
    }

    const fieldName = editingField.name || editingField.label.toLowerCase().replace(/\s+/g, '_');
    const updatedField = { ...editingField, name: fieldName };

    const existingIndex = fields.findIndex(f => f.tempId === editingField.tempId);
    if (existingIndex >= 0) {
      // Editing existing field
      setFields(fields.map(f => f.tempId === editingField.tempId ? updatedField : f));
    } else {
      // New field
      setFields([...fields, updatedField]);
    }

    setEditingField(null);
    setError(null);
  };

  const handleDeleteField = (tempId: string) => {
    setFields(fields.filter(f => f.tempId !== tempId));
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= fields.length) return;
    
    // Swap fields
    const currentField = newFields[index];
    const targetField = newFields[newIndex];
    if (!currentField || !targetField) return;

    newFields[index] = targetField;
    newFields[newIndex] = currentField;
    
    // Update display_order
    const updatedFields = newFields.map((field, idx) => ({
      ...field,
      display_order: idx,
    }));
    
    setFields(updatedFields);
  };

  const handleSubmit = async (isDraft = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.district_id) {
        throw new Error('Your account is not assigned to a district');
      }

      const createdForm = await permissionSlipApiService.createForm({
        name: formData.title,
        title: formData.title,
        district_id: user.district_id,
        school_id: user.school_id,
        description: formData.description,
        category: formData.category,
        requires_payment: formData.requires_payment,
        cost: formData.requires_payment ? formData.payment_amount : undefined,
        due_date: formData.expires_at || undefined,
      });

      for (const field of fields) {
        await permissionSlipApiService.addField(createdForm.id, {
          field_type: field.field_type,
          field_name: field.name,
          field_label: field.label,
          field_description: field.description || field.help_text,
          placeholder_text: field.placeholder,
          display_order: field.display_order,
          is_required: field.is_required,
          field_options: field.options?.map((option) => ({
            label: option.label,
            value: option.value || option.label,
          })),
          default_value:
            typeof field.default_value === 'string'
              ? field.default_value
              : undefined,
        });
      }

      if (formData.is_template) {
        await permissionSlipApiService.createTemplate(createdForm.id, {
          name: formData.template_name || formData.title,
          title: formData.title,
          description:
            formData.template_description || formData.description,
          category: formData.category,
        });
      }

      if (!isDraft) {
        await permissionSlipApiService.activateForm(createdForm.id);
      }
      
      navigate('/permission-slips');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create form');
    } finally {
      setLoading(false);
    }
  };

  const renderBasicInfo = () => (
    <Box>
      <TextField
        fullWidth
        label="Form Title"
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        margin="normal"
        required
        placeholder="e.g., Science Museum Field Trip Permission"
      />
      
      <TextField
        fullWidth
        label="Description"
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        margin="normal"
        required
        multiline
        rows={3}
        placeholder="Brief description of what this permission slip is for"
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Category</InputLabel>
        <Select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          label="Category"
        >
          {categories.map((cat) => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.requires_payment}
              onChange={(e) => setFormData({ ...formData, requires_payment: e.target.checked })}
            />
          }
          label="This form requires payment"
        />
      </Box>

      {formData.requires_payment && (
        <Box sx={{ mt: 2, pl: 4 }}>
          <TextField
            label="Payment Amount"
            value={formData.payment_amount}
            onChange={(e) => setFormData({ ...formData, payment_amount: parseFloat(e.target.value) || 0 })}
            margin="normal"
            type="number"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mr: 2 }}
          />
          <TextField
            label="Payment Description"
            value={formData.payment_description}
            onChange={(e) => setFormData({ ...formData, payment_description: e.target.value })}
            margin="normal"
            placeholder="e.g., Bus transportation and admission fees"
            sx={{ flexGrow: 1 }}
          />
        </Box>
      )}

      <TextField
        fullWidth
        label="Expiration Date"
        type="date"
        value={formData.expires_at}
        onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
        margin="normal"
        InputLabelProps={{ shrink: true }}
        helperText="When should this form stop accepting submissions?"
      />

      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_template}
              onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
            />
          }
          label="Save as template for future use"
        />
      </Box>

      {formData.is_template && (
        <Box sx={{ mt: 2, pl: 4 }}>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.template_name}
            onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
            margin="normal"
            placeholder="e.g., Standard Field Trip Template"
          />
          <TextField
            fullWidth
            label="Template Description"
            value={formData.template_description}
            onChange={(e) => setFormData({ ...formData, template_description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            placeholder="Describe when to use this template"
          />
        </Box>
      )}
    </Box>
  );

  const renderFormFields = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Form Fields</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowFieldPicker(true)}
        >
          Add Field
        </Button>
      </Box>

      {fields.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No fields added yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start building your form by adding fields
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowFieldPicker(true)}
            >
              Add Your First Field
            </Button>
          </CardContent>
        </Card>
      ) : (
        <List>
          {fields.map((field, index) => (
            <ListItem
              key={field.tempId}
              sx={{
                mb: 1,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <>{fieldTypeIcons[field.field_type]}</>
              </ListItemIcon>
              <ListItemText
                primary={field.label || 'Untitled Field'}
                secondary={fieldTypeLabels[field.field_type]}
              />
              {field.is_required && (
                <Chip label="Required" size="small" color="error" sx={{ mr: 1 }} />
              )}
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleMoveField(index, 'up')}
                  disabled={index === 0}
                  size="small"
                >
                  <MoveUpIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => handleMoveField(index, 'down')}
                  disabled={index === fields.length - 1}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <MoveDownIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => setEditingField(field)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => handleDeleteField(field.tempId)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  const renderReview = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Form
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Basic Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Title:</strong> {formData.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Description:</strong> {formData.description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Category:</strong> {categories.find(c => c.value === formData.category)?.label}
          </Typography>
          {formData.requires_payment && (
            <Typography variant="body2" color="text.secondary">
              <strong>Payment:</strong> ${formData.payment_amount} - {formData.payment_description}
            </Typography>
          )}
          {formData.expires_at && (
            <Typography variant="body2" color="text.secondary">
              <strong>Expires:</strong> {new Date(formData.expires_at).toLocaleDateString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Form Fields ({fields.length})
          </Typography>
          <List dense>
            {fields.map((field, index) => (
              <ListItem key={field.tempId}>
                <ListItemIcon>
                  <>{fieldTypeIcons[field.field_type]}</>
                </ListItemIcon>
                <ListItemText
                  primary={`${index + 1}. ${field.label}`}
                  secondary={field.is_required ? 'Required' : 'Optional'}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/permission-slips')}
          sx={{ mb: 2 }}
        >
          Back to Permission Slips
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          Create Permission Slip Form
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderBasicInfo()}
        {activeStep === 1 && renderFormFields()}
        {activeStep === 2 && renderReview()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSubmit(true)}
                  disabled={loading}
                >
                  Save as Draft
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => handleSubmit(false)}
                  disabled={loading}
                >
                  Create & Activate
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
              >
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Field Picker Dialog */}
      <Dialog
        open={showFieldPicker}
        onClose={() => setShowFieldPicker(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose Field Type</DialogTitle>
        <DialogContent>
          <List>
            {Object.entries(fieldTypeLabels).map(([type, label]) => (
              <ListItem
                key={type}
                onClick={() => handleAddField(type as FieldType)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <ListItemIcon>
                  <>{fieldTypeIcons[type as FieldType]}</>
                </ListItemIcon>
                <ListItemText primary={label} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldPicker(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog
        open={!!editingField}
        onClose={() => setEditingField(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingField?.tempId.startsWith('field_') ? 'Add' : 'Edit'} Field
        </DialogTitle>
        <DialogContent>
          {editingField && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Field Label"
                value={editingField.label}
                onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                margin="normal"
                required
                placeholder="e.g., Student Name"
              />
              
              <TextField
                fullWidth
                label="Field Name (for data)"
                value={editingField.name}
                onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                margin="normal"
                placeholder="e.g., student_name (auto-generated if empty)"
                helperText="Used for data storage - leave empty to auto-generate"
              />

              <TextField
                fullWidth
                label="Placeholder Text"
                value={editingField.placeholder}
                onChange={(e) => setEditingField({ ...editingField, placeholder: e.target.value })}
                margin="normal"
                placeholder="e.g., Enter student's full name"
              />

              <TextField
                fullWidth
                label="Help Text"
                value={editingField.help_text}
                onChange={(e) => setEditingField({ ...editingField, help_text: e.target.value })}
                margin="normal"
                placeholder="e.g., As it appears on school records"
                multiline
                rows={2}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={editingField.is_required}
                    onChange={(e) => setEditingField({ ...editingField, is_required: e.target.checked })}
                  />
                }
                label="Required field"
                sx={{ mt: 2 }}
              />

              {(editingField.field_type === FieldType.RADIO || editingField.field_type === FieldType.SELECT) && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Options
                  </Typography>
                  {editingField.options?.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        value={option.label}
                        onChange={(e) => {
                          const newOptions = [...(editingField.options || [])];
                          newOptions[index] = { ...option, label: e.target.value };
                          setEditingField({ ...editingField, options: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                      />
                      <IconButton
                        onClick={() => {
                          const newOptions = editingField.options?.filter((_, i) => i !== index);
                          setEditingField({ ...editingField, options: newOptions });
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newOptions = [...(editingField.options || []), { value: '', label: '' }];
                      setEditingField({ ...editingField, options: newOptions });
                    }}
                  >
                    Add Option
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingField(null)}>Cancel</Button>
          <Button onClick={handleSaveField} variant="contained">
            Save Field
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}; 